import type { AIModel, ApiProtocol, ModelHosting } from "@/types";

// ============================================================================
// Endpoint (hosting) + API protocol configuration
//
// Shared between AddModelModal and ConfigureModelModal so the two-select
// "Endpoint + API Protocol" UI, its option lists, protocol-aware hints, and the
// form<->payload mapping live in exactly one place.
// ============================================================================

export interface HostingOption {
  value: ModelHosting;
  label: string;
  description: string;
}

/** Endpoint presets plus the "custom" escape hatch (former endpoint override). */
export const HOSTING_OPTIONS: HostingOption[] = [
  {
    value: "azure_openai_eastus2",
    label: "Azure OpenAI (East US 2)",
    description: "Primary OpenAI resource — GPT models, Whisper, image generation",
  },
  {
    value: "azure_openai_westus",
    label: "Azure OpenAI (West US)",
    description: "Deep Research models",
  },
  {
    value: "foundry_eastus2",
    label: "Azure AI Foundry (East US 2)",
    description: "Claude, GPT, Grok, DeepSeek and other models via AI Foundry",
  },
  {
    value: "custom",
    label: "Custom endpoint…",
    description: "Point this model at an explicit endpoint URL + API key",
  },
];

export interface ApiProtocolOption {
  value: ApiProtocol;
  label: string;
  description: string;
}

export const API_PROTOCOL_OPTIONS: ApiProtocolOption[] = [
  {
    value: "openai_responses",
    label: "OpenAI Responses",
    description: "responses.create — GPT-5.x and most Azure OpenAI / Foundry OpenAI models",
  },
  {
    value: "openai_completions",
    label: "OpenAI Chat Completions",
    description: "chat.completions.create — model-router, Grok, DeepSeek",
  },
  {
    value: "anthropic_messages",
    label: "Anthropic Messages",
    description: "Anthropic SDK — Claude models on Foundry",
  },
];

/** The URL path suffix a preset endpoint expects for a given protocol. */
export function expectedEndpointSuffix(protocol: ApiProtocol): string {
  return protocol === "anthropic_messages" ? "/anthropic" : "/openai/v1";
}

/** A canonical example custom URL for a protocol (used as placeholder + hint). */
export function exampleEndpointUrl(protocol: ApiProtocol): string {
  return `https://<resource>.services.ai.azure.com${expectedEndpointSuffix(protocol)}`;
}

/**
 * Warn-only check: returns a hint string when a custom URL doesn't end with the
 * suffix expected for the selected protocol, otherwise null. Never blocks.
 */
export function endpointSuffixWarning(url: string, protocol: ApiProtocol): string | null {
  let trimmed = url.trim();
  // Strip trailing slashes without a regex (avoids super-linear backtracking).
  while (trimmed.endsWith("/")) {
    trimmed = trimmed.slice(0, -1);
  }
  if (!trimmed) return null;
  const suffix = expectedEndpointSuffix(protocol);
  if (trimmed.endsWith(suffix)) return null;
  return `This usually ends in ${suffix} for the ${
    protocol === "anthropic_messages" ? "Anthropic" : "OpenAI"
  } protocol — double-check your endpoint.`;
}

/**
 * Derive the legacy `provider` string from api_protocol + hosting. The backend
 * still accepts/stores provider during the transition; sending a consistent
 * value keeps older readers correct.
 */
export function deriveLegacyProvider(apiProtocol: ApiProtocol, hosting: ModelHosting): string {
  if (apiProtocol === "anthropic_messages") return "anthropic_foundry";
  if (hosting === "foundry_eastus2") return "azure_foundry";
  return "azure_openai";
}

/** The endpoint/protocol slice of the model form. */
export interface EndpointConfig {
  hosting: ModelHosting;
  api_protocol: ApiProtocol;
  endpoint_url: string;
}

/** Read the endpoint/protocol config off an existing model (camel or snake). */
export function readEndpointConfig(model: AIModel): EndpointConfig {
  return {
    hosting: model.hosting ?? "azure_openai_eastus2",
    api_protocol: model.apiProtocol ?? model.api_protocol ?? "openai_responses",
    endpoint_url: model.endpointUrl || model.endpoint_url || "",
  };
}

/**
 * Build the endpoint/protocol portion of an API payload. For non-custom
 * hostings, endpoint_url is cleared (null) so the backend resolves from
 * settings. For custom, the entered URL is sent verbatim.
 */
export function toEndpointPayload(config: EndpointConfig): Record<string, unknown> {
  return {
    api_protocol: config.api_protocol,
    hosting: config.hosting,
    provider: deriveLegacyProvider(config.api_protocol, config.hosting),
    endpoint_url: config.hosting === "custom" ? config.endpoint_url || null : null,
  };
}
